/**
 * useAvatarAnalytics.ts
 *
 * Forwards the avatar SDK's existing observability window events to the
 * @camlin/analytics ingest endpoint (Phase P3), so avatar health + live-session
 * lifecycle show up in the same cross-platform analytics store as runtime flow
 * events and voice call events.
 *
 * It is intentionally a thin FORWARDER over events the SDK already dispatches:
 *   - `avatar:health`      (from useAvatarHealth) -> `avatar.health` CamlinEvent
 *   - `avatar:live:status` (from useHostedLiveController) -> session lifecycle
 *
 * Listening to the window events (rather than reading hook state) keeps this
 * decoupled: no new producer logic in the health/lifecycle hooks, and the same
 * forwarder works for every consumer surface. Best-effort + never throws — the
 * client swallows all network errors.
 *
 * Correlation: `sessionId` should be the cross-product session/handoff id so an
 * avatar event stitches to the voice call + runtime flow for the same
 * interaction.
 */

import { useEffect, useMemo, useRef } from 'react';

import {
  AvatarAnalyticsClient,
  type AvatarAnalyticsEvent,
} from '../analytics/avatarAnalyticsClient';
import type { AvatarHealthState } from './useAvatarHealth';

export interface UseAvatarAnalyticsOptions {
  /** Runtime base URL; the ingest endpoint is derived as `{runtimeUrl}/api/analytics/events`. */
  runtimeUrl?: string | null;
  /** Cross-product session / handoff id for correlation. */
  sessionId?: string | null;
  projectToken?: string | null;
  diagramId?: string | null;
  /** Disable forwarding entirely (default true). */
  enabled?: boolean;
}

function healthSeverityToEvent(
  overall: AvatarHealthState['overall'],
): AvatarAnalyticsEvent['severity'] {
  if (overall === 'fail') return 'fail';
  if (overall === 'warn') return 'warn';
  return 'ok';
}

export function useAvatarAnalytics({
  runtimeUrl,
  sessionId,
  projectToken,
  diagramId,
  enabled = true,
}: UseAvatarAnalyticsOptions): void {
  const client = useMemo(
    () => (enabled ? new AvatarAnalyticsClient(runtimeUrl) : null),
    [enabled, runtimeUrl],
  );

  // Keep the latest correlation ids in a ref so the window listeners (mounted
  // once) always read current values without re-subscribing on every change.
  const corrRef = useRef({ sessionId, projectToken, diagramId });
  corrRef.current = { sessionId, projectToken, diagramId };

  // De-dupe identical consecutive health states + lifecycle statuses so we
  // don't flood the ingest endpoint when upstream signals churn.
  const lastHealthKeyRef = useRef<string | null>(null);
  const lastStatusRef = useRef<string | null>(null);

  useEffect(() => {
    if (!client || !client.enabled || typeof window === 'undefined') return;

    const corr = () => corrRef.current;

    const handleHealth = (evt: Event) => {
      const detail = (evt as CustomEvent<AvatarHealthState>).detail;
      if (!detail) return;
      const key = `${detail.overall}:${detail.issues.map((i) => i.id).sort().join(',')}`;
      if (lastHealthKeyRef.current === key) return;
      lastHealthKeyRef.current = key;
      const { sessionId: sid, projectToken: pt, diagramId: dg } = corr();
      client.emit({
        eventId: `${sid ?? 'anon'}:avatar.health:${key}:${detail.lastUpdated}`,
        type: 'avatar.health',
        channel: 'avatar',
        severity: healthSeverityToEvent(detail.overall),
        sessionId: sid ?? undefined,
        projectToken: pt ?? undefined,
        diagramId: dg ?? undefined,
        props: {
          overall: detail.overall,
          issues: detail.issues.map((i) => i.id).join(',') || 'none',
        },
        metrics: { issueCount: detail.issues.length },
      });
    };

    const handleStatus = (evt: Event) => {
      const detail = (evt as CustomEvent<{ status?: string | null; error?: string | null }>).detail;
      const status = detail?.status ?? null;
      if (!status || lastStatusRef.current === status) return;
      const prev = lastStatusRef.current;
      lastStatusRef.current = status;
      const { sessionId: sid, projectToken: pt, diagramId: dg } = corr();

      // Emit a session.started the first time we see a connected status, and a
      // session.ended on terminal statuses. Intermediate transitions are left
      // to the health surface to avoid event spam.
      let type: string | null = null;
      let severity: AvatarAnalyticsEvent['severity'] = 'info';
      if ((status === 'ready' || status === 'listening') && prev === 'connecting') {
        type = 'avatar.live.session.started';
        severity = 'ok';
      } else if (status === 'error') {
        type = 'avatar.live.session.ended';
        severity = 'error';
      } else if (status === 'ended' && prev && prev !== 'ended' && prev !== 'idle') {
        type = 'avatar.live.session.ended';
        severity = 'ok';
      } else if (status === 'idle' && prev && prev !== 'idle' && prev !== 'ended') {
        type = 'avatar.live.session.ended';
        severity = 'ok';
      }
      if (!type) return;

      client.emit({
        eventId: `${sid ?? 'anon'}:${type}:${status}`,
        type,
        channel: 'avatar',
        severity,
        sessionId: sid ?? undefined,
        projectToken: pt ?? undefined,
        diagramId: dg ?? undefined,
        props: { status, prevStatus: prev ?? 'none' },
      });
    };

    window.addEventListener('avatar:health', handleHealth as EventListener);
    window.addEventListener('avatar:live:status', handleStatus as EventListener);

    // Best-effort flush when the page is being hidden / unloaded.
    const handlePageHide = () => {
      void client.flush();
    };
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      window.removeEventListener('avatar:health', handleHealth as EventListener);
      window.removeEventListener('avatar:live:status', handleStatus as EventListener);
      window.removeEventListener('pagehide', handlePageHide);
      void client.flush();
    };
  }, [client]);
}

export default useAvatarAnalytics;
